// Devices browse state: status/model filters + opaque-cursor pagination.
//
// Mirrors useUserBrowse (FACTS PACK §3c uses the same cursor convention as users):
// the API cursor is forward-only & opaque, so a client stack of the cursors that
// opened each page enables prev/next. pageCursors[0] === undefined = first page.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../lib/api';
import { listDevices } from './devicesApi';
import type { AdminDevice } from './devicesApi';

const PAGE_SIZE = 25;

export interface DeviceFilters {
  status: string;
  model: string;
  /** common#24/#79 base ecosystem (device.base_ecosystem_id); '' = all. */
  ecosystem: string;
}

export interface DeviceBrowseState {
  devices: AdminDevice[] | null;
  loading: boolean;
  error: unknown; // raw error so the page can pass it to <ErrorState/> (403-aware)
  pageIndex: number; // 0-based
  hasPrev: boolean;
  hasNext: boolean;
  reload: () => void;
  next: () => void;
  prev: () => void;
}

export function useDeviceBrowse(filters: DeviceFilters): DeviceBrowseState {
  const [devices, setDevices] = useState<AdminDevice[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [pageCursors, setPageCursors] = useState<(string | undefined)[]>([undefined]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const alive = useRef(true);
  const reqSeq = useRef(0);

  const fetchPage = useCallback(
    async (cursor: string | undefined) => {
      const seq = ++reqSeq.current;
      setLoading(true);
      setError(null);
      try {
        const page = await listDevices({
          status: filters.status || undefined,
          model: filters.model || undefined,
          ecosystem: filters.ecosystem || undefined,
          limit: PAGE_SIZE,
          cursor: cursor || undefined,
        });
        if (!alive.current || seq !== reqSeq.current) return;
        setDevices(page.devices);
        setNextCursor(page.nextCursor);
      } catch (e) {
        if (!alive.current || seq !== reqSeq.current) return;
        setDevices(null);
        setNextCursor(null);
        setError(e instanceof ApiError ? e : (e ?? new Error('載入失敗')));
      } finally {
        if (alive.current && seq === reqSeq.current) setLoading(false);
      }
    },
    [filters.status, filters.model, filters.ecosystem]
  );

  // Reset to page 0 whenever filters change.
  useEffect(() => {
    alive.current = true;
    setPageCursors([undefined]);
    void fetchPage(undefined);
    return () => {
      alive.current = false;
    };
  }, [fetchPage]);

  const reload = useCallback(() => {
    const current = pageCursors[pageCursors.length - 1];
    void fetchPage(current);
  }, [fetchPage, pageCursors]);

  const next = useCallback(() => {
    if (!nextCursor) return;
    setPageCursors((s) => [...s, nextCursor]);
    void fetchPage(nextCursor);
  }, [fetchPage, nextCursor]);

  const prev = useCallback(() => {
    setPageCursors((s) => {
      if (s.length <= 1) return s;
      const reduced = s.slice(0, -1);
      void fetchPage(reduced[reduced.length - 1]);
      return reduced;
    });
  }, [fetchPage]);

  return {
    devices,
    loading,
    error,
    pageIndex: pageCursors.length - 1,
    hasPrev: pageCursors.length > 1,
    hasNext: !!nextCursor,
    reload,
    next,
    prev,
  };
}
